// Modules
import React, { useEffect, useState, useContext } from "react";
import PropTypes from "prop-types";
import Slider from "@material-ui/core/Slider";
import NumberFormat from "react-number-format";

// Context
import ApiContext from "../../../contexts/ApiContext";

// Local
import Switch from "../../utils/Switch";
import config from "../../../config/environment";

// Assets
import "./simulator.scss";
import CloseIcon from "../../../assets/img/svg/Close_icon.jsx";
import FilterIcon from "../../../assets/img/svg/FilterIcon";

/**
 * Computes a thousands rounded value,
 * used for the "mise de depart" field
 * @param {Number} : x (value to round)
 * @param {Number} : y (how to round 10/100/1000 etc)
 * @returns {String} : relative time from now
 */
function numRoundMultiple(x, y) {
	return Math.round(x / y) * y;
  }





const Simulator = (props) => {
	const {
		loader,
		valuesSimulation,
		setValuesSimulation,
		stateSwitch,
		setStateSwitch,
		total_acquisition,
	} = props;
	const {
		frais_courtage,
		mise_depart,
		duree_emprunt,
		taux_emprunt,
		taux_assurance,
		duree_revente,
	} = valuesSimulation;
	const {
		fraisCourtage,
		/* V2 or V3 */
		// effortEpargne,
		// equilibre
	} = stateSwitch;
	const { isMobile, isTablet } = useContext(ApiContext);
	const [isOpen, setIsOpen] = useState(false);

	/* Switch states */
	const [minMaxTE, setMinMaxTE] = useState({ min: 0.90, max: 2.00 }); // Taux d'emprunt
	const [minMD, setMinMD] = useState(0); // Mise de départ
	const [inputState, setInputState] = useState({
		error: false,
		message: null,
	});

	/**
	 * Changes the value minimum & maximum of the slider
	 * "Taux d'emprunt" when "Duree d'emprunt" is changed.
	 */
	useEffect(() => {
		switch (duree_emprunt) {
			case 5:
				setMinMaxTE({ min: 0.30, max: 1.50 });
				break;
			case 10:
				setMinMaxTE({ min: 0.50, max: 1.80 });
				break;
			case 15:
				setMinMaxTE({ min: 0.60, max: 1.80 });
				break;
			case 20:
				setMinMaxTE({ min: 0.90, max: 2.00 });
				break;
			case 25:
				setMinMaxTE({ min: 1.00, max: 2.50 });
				break;

			default:
				setMinMaxTE({ min: 0.90, max: 2.00 });
				break;
		}
	}, [duree_emprunt]);

	/**
	 * Changes the minimum value of the slider "Mise de départ"
	 * when "total_acquisition" is defined.
	 */
	useEffect(() => {
		if (total_acquisition)
			setMinMD(numRoundMultiple(0.75 * ((total_acquisition / 100) * config.SIMULATION.mise_depart_percent),1000));
	}, [total_acquisition]);

	/**
	* Callback function from Material UI that is fired when the slider's value changed.
	* @param {Event} event
	* @param {String} name : name of the key that changed in the "valuesSimulation" object.
	* @param {String} newValue : new value to change in the "valuesSimulation" object.
	*/
	const handleChangeSliders = (name) => (event, newValue) => {
		setValuesSimulation({
			...valuesSimulation,
			[name]: newValue,
		});
	};

	/**
	 * Handles the change of state for switch
	 * @param {Event} event
	 * @param {String} name : name of the key to change from the "stateSwitch" object
	 */
	const handleChangeSwitch = (event, name) => {
		// If the switch is off for "frais_courtage", we change its value to 0 for an impact in the results.
		if (name === "fraisCourtage") {
			if (stateSwitch.fraisCourtage === true)
				setValuesSimulation({ ...valuesSimulation, frais_courtage: 0 });
			else if (stateSwitch.fraisCourtage === false)
				setValuesSimulation({ ...valuesSimulation, frais_courtage: 2000 });
		}
		setStateSwitch({
			...stateSwitch,
			[name]: !stateSwitch[name],
		});
	};

	/**
	* Function fired by Material UI which returns a string value that provides
	* a user-friendly name for the current value of the slider.
	* @param {Number} value : The thumb label's value to format.
	* @param {Number} index : The thumb label's index to format.
	* @returns {String} : value formatted with €
	*/
	const parseValue = (value, index) => {
		return `${new Intl.NumberFormat("fr-FR", {
			style: "currency",
			currency: "EUR",
			maximumFractionDigits: 0, 
			minimumFractionDigits: 0,
		}).format(value)}`;
	};

	/**
	 * Handle the change of value for the "Mise de départ" input, next to the slider.
	 * @param {Object} valueObj : value object sent by NumberFormat
	 */
	const handleChangeInputMD = (valueObj) => {
		const newValue = parseInt(valueObj.value);

		if (newValue < minMD)
			setInputState({ error: true, message: "Mise de départ faible, moins susceptible d’être acceptée par les banques" });
		else
			setInputState({ error: false, message: "Mise de départ dans la norme du marché" });

		setValuesSimulation({ ...valuesSimulation, mise_depart: newValue });
	};

	/**
	 * Handle the change of value for the "Durée de revente" input, next to the slider.
	 * @param {Object} valueObj : value object sent by NumberFormat
	 */
	 const handleChangeInputDR = (valueObj) => {
		const newValue = parseInt(valueObj.value);

		setValuesSimulation({ ...valuesSimulation, duree_revente: newValue });
	};

	const toggleOpenSimulator = () => {
		setIsOpen(!isOpen);
	};

	return (
		((isMobile === true || isTablet === true) && isOpen === true) || (isMobile === false && isTablet === false)
			? (
				<div className={`balance-sheet-simulation-container ${isMobile === true || isTablet === true ? "mobile" : ""} ${isTablet === true ? "tablet" : ""}`}>
					{/* Sliders */}
					<div className="sliders-container">
						{isMobile === true || isTablet === true
							? (
								<React.Fragment>
									<CloseIcon clssName="close-icon" onClick={toggleOpenSimulator} />
									<h5 className="title">Simulez votre investissement</h5>
								</React.Fragment>
							) : null}
						<Switch
							isActive={fraisCourtage}
							onClick={(e) => handleChangeSwitch(e, "fraisCourtage")}
							clssName="slider-container switch"
							label={(
								<h6>
									Frais de courtage
									<span>
										({new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0, minimumFractionDigits: 0 }).format(frais_courtage)})
							</span>
								</h6>
							)}
							loader={loader}
						/>

						<div className="slider-container">
							<h6>Mise de départ</h6>
							<div className="slider-wrapper">
								{/* Input number */}
								<NumberFormat
									className="input-number"
									value={mise_depart}
									type="text"
									onValueChange={handleChangeInputMD}
									allowNegative={false}
									thousandSeparator=" "
									allowedDecimalSeparators={[".", ","]}
									suffix={" €"}
								/>
								<Slider
									value={mise_depart}
									name="mise_depart"
									valueLabelDisplay="auto"
									valueLabelFormat={parseValue}
									min={minMD}
									max={total_acquisition ?? 0}
									step={1000}
									onChange={handleChangeSliders("mise_depart")}
									aria-labelledby="continuous-slider"
									className="slider mise-de-depart"
								/>
								<p>
									{new Intl.NumberFormat("fr-FR", {
										style: "currency",
										currency: "EUR",
										maximumFractionDigits: 0, 
										minimumFractionDigits: 0,
									}).format(total_acquisition ?? 0)}
								</p>
							</div>
							{inputState.message !== null
								? <p className={`input-message ${inputState.error === true ? "error" : ""}`}>{inputState.message}</p>
								: null}
						</div>

						<div className="slider-container">
							<h6>Durée d'emprunt</h6>
							<div className="slider-wrapper">
								<p>5 ans</p>
								<Slider
									value={duree_emprunt}
									name="duree_emprunt"
									getAriaValueText={parseValue}
									valueLabelDisplay="auto"
									min={5}
									max={25}
									marks
									step={5}
									onChange={handleChangeSliders("duree_emprunt")}
									aria-labelledby="continuous-slider"
									className="slider"
								/>
								<p>25 ans</p>
							</div>
						</div>

						<div className="slider-container">
							<h6>
								Taux d'emprunt
            		<span>(sur {duree_emprunt} ans)</span>
							</h6>
							<div className="slider-wrapper">
								<p>{minMaxTE.min.toFixed(2)}%</p>
								<Slider
									value={taux_emprunt}
									name="taux_emprunt"
									getAriaValueText={parseValue}
									valueLabelDisplay="auto"
									min={minMaxTE.min}
									step={0.10}
									max={minMaxTE.max}
									onChange={handleChangeSliders("taux_emprunt")}
									aria-labelledby="continuous-slider"
									className="slider"
								/>
								<p>{minMaxTE.max.toFixed(2)}%</p>
							</div>
						</div>

						<div className="slider-container">
							<h6>Taux d'assurance emprunteur</h6>
							<div className="slider-wrapper">
								<p>0.07%</p>
								<Slider
									value={taux_assurance}
									name="taux_assurance"
									getAriaValueText={parseValue}
									valueLabelDisplay="auto"
									min={0.07}
									step={0.01}
									max={0.65}
									onChange={handleChangeSliders("taux_assurance")}
									aria-labelledby="continuous-slider"
									className="slider"
								/>
								<p>0.65%</p>
							</div>
						</div>

						<div className="slider-container">
							<h6>Revente prévue dans :</h6>
							<div className="slider-wrapper">
								<NumberFormat
									className="input-number"
									value={duree_revente}
									type="text"
									onValueChange={handleChangeInputDR}
									allowNegative={false}
									thousandSeparator=" "
									allowedDecimalSeparators={[".", ","]}
									suffix={" ans"}
								/>
								<Slider
									value={duree_revente}
									name="duree_revente"
									getAriaValueText={parseValue}
									valueLabelDisplay="auto"
									min={0}
									max={50}
									onChange={handleChangeSliders("duree_revente")}
									aria-labelledby="continuous-slider"
									className="slider"
								/>
								<p>50 ans</p>
							</div>
						</div>
					</div>

					{/* V2 */}
					{/* Switches */}
					{/* <div className="switches-container">
				<div className="switches-wrapper">
					<Switch
						isActive={fraisCourtage}
						onClick={(e) => handleChangeSwitch(e, "fraisCourtage")}
						clssName="switch"
						label="Frais de courtages"
						loader={loader}
					/>
					<Switch
            isActive={stateSwitch.effortEpargne}
            onClick={(e) => handleChangeSwitch(e, "effortEpargne")}
            clssName="switch"
            label="Effort d'épargne"
            loader={loader}
          />
          <Switch
            isActive={stateSwitch.equilibre}
            onClick={(e) => handleChangeSwitch(e, "equilibre")}
            clssName="switch"
            label="Équilibre"
            loader={loader}
          />
				</div>
				<CustomRadio
          options={["Annuel", "Mensuel"]}
          nameGroup={"periodicity"}
          checked={selectedRadio}
          onChange={handleChangeRadio}
          loader={loader}
          clssName="radio-periodicity"
        />
			</div> */}
				</div>
			) : (
				<div className={`balance-sheet-simulation-container mobile reduced ${isTablet === true ? "tablet" : ""}`}>
					<div
						className="simulator-reduced-wrapper"
						onClick={toggleOpenSimulator}
					>
						<FilterIcon clssName="filter-icon" />
					</div>
				</div>
			)
	);
};

Simulator.propTypes = {
	loader: PropTypes.bool.isRequired,
	valuesSimulation: PropTypes.exact({
		frais_courtage: PropTypes.number.isRequired,
		mise_depart: PropTypes.number.isRequired,
		duree_emprunt: PropTypes.number.isRequired,
		taux_emprunt: PropTypes.number.isRequired,
		taux_assurance: PropTypes.number.isRequired,
		duree_revente: PropTypes.number.isRequired,
	}),
	setValuesSimulation: PropTypes.func.isRequired,
	stateSwitch: PropTypes.exact({
		fraisCourtage: PropTypes.bool.isRequired,
		effortEpargne: PropTypes.bool.isRequired,
		equilibre: PropTypes.bool.isRequired,
	}),
	setStateSwitch: PropTypes.func.isRequired,
};

export default Simulator;